import type { Sale } from "../../types";

/** Bearer token of the current session (see api/src/Auth.php). */
export const TOKEN_STORAGE_KEY = "ssp.token";
/** The logged-in user as returned by POST /auth/login and GET /auth/me. */
export const USER_STORAGE_KEY = "ssp.user";

/**
 * localStorage can throw (private mode, disabled site data), so every access
 * is guarded: a failing storage must never break the app.
 */
const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore: the session then simply does not survive a reload.
  }
};

const remove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore
  }
};

export const getToken = (): string | null => read(TOKEN_STORAGE_KEY);

export const setToken = (token: string): void =>
  write(TOKEN_STORAGE_KEY, token);

export const getStoredUser = (): Sale | null => {
  const raw = read(USER_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as Sale;
  } catch {
    return null;
  }
};

export const setStoredUser = (user: Sale): void =>
  write(USER_STORAGE_KEY, JSON.stringify(user));

/** Forget the token and the identity (logout, or an expired session). */
export const clearSession = (): void => {
  remove(TOKEN_STORAGE_KEY);
  remove(USER_STORAGE_KEY);
};
